// Theme definitions for light and dark mode

export const lightTheme = {
  // Background colors
  bgPrimary: '#ffffff',
  bgSecondary: '#f2f5fc',
  bgTertiary: '#e8ecf7',

  // Text colors
  textPrimary: '#2b3b7c',
  textSecondary: '#5e5b5b',
  textMuted: '#8a8a8a',

  // Brand colors
  primary: '#2b3b7c',
  accent: '#d93a35',
  accentHover: '#c12f2a',

  // Card and surface colors
  cardBg: '#ffffff',
  cardBorder: 'rgba(43, 59, 124, 0.1)',

  // Shadow colors
  shadow: 'rgba(0, 0, 0, 0.1)',
  shadowMedium: 'rgba(0, 0, 0, 0.15)',
  shadowLarge: 'rgba(0, 0, 0, 0.2)',

  // Glass effect
  glassBg: 'rgba(255, 255, 255, 0.7)',
  glassBorder: 'rgba(255, 255, 255, 0.2)',

  // Gradient
  gradientStart: '#f2f5fc',
  gradientEnd: '#ffffff',

  // Overlay
  overlay: 'rgba(0, 0, 0, 0.5)',

  // Header
  headerBg: '#ffffff',
  headerBgScrolled: 'rgba(255, 255, 255, 0.95)',
};

export const darkTheme = {
  // Background colors
  bgPrimary: '#0f1419',
  bgSecondary: '#1a1f2e',
  bgTertiary: '#252d3d',

  // Text colors
  textPrimary: '#e8eaed',
  textSecondary: '#9aa0a6',
  textMuted: '#6b7280',

  // Brand colors
  primary: '#6b7fff',
  accent: '#ff4d4d',
  accentHover: '#ff6666',

  // Card and surface colors
  cardBg: '#1e2532',
  cardBorder: 'rgba(255, 255, 255, 0.1)',

  // Shadow colors
  shadow: 'rgba(0, 0, 0, 0.3)',
  shadowMedium: 'rgba(0, 0, 0, 0.4)',
  shadowLarge: 'rgba(0, 0, 0, 0.5)',

  // Glass effect
  glassBg: 'rgba(30, 37, 50, 0.8)',
  glassBorder: 'rgba(255, 255, 255, 0.1)',

  // Gradient
  gradientStart: '#1a1f2e',
  gradientEnd: '#0f1419',

  // Overlay
  overlay: 'rgba(0, 0, 0, 0.7)',

  // Header
  headerBg: '#0f1419',
  headerBgScrolled: 'rgba(15, 20, 25, 0.95)',
};

export type Theme = typeof lightTheme;

// CSS custom properties mapping
export const getCSSVariables = (theme: Theme): string => {
  return `
    --bg-primary: ${theme.bgPrimary};
    --bg-secondary: ${theme.bgSecondary};
    --bg-tertiary: ${theme.bgTertiary};
    --text-primary: ${theme.textPrimary};
    --text-secondary: ${theme.textSecondary};
    --text-muted: ${theme.textMuted};
    --primary: ${theme.primary};
    --accent: ${theme.accent};
    --accent-hover: ${theme.accentHover};
    --card-bg: ${theme.cardBg};
    --card-border: ${theme.cardBorder};
    --shadow: ${theme.shadow};
    --shadow-medium: ${theme.shadowMedium};
    --shadow-large: ${theme.shadowLarge};
    --glass-bg: ${theme.glassBg};
    --glass-border: ${theme.glassBorder};
    --gradient-start: ${theme.gradientStart};
    --gradient-end: ${theme.gradientEnd};
    --overlay: ${theme.overlay};
    --header-bg: ${theme.headerBg};
    --header-bg-scrolled: ${theme.headerBgScrolled};
  `;
};

// Transition definitions for theme changes
export const themeTransition = {
  duration: '0.3s',
  timing: 'ease-in-out',
  properties: 'background-color, color, border-color, box-shadow, fill, stroke',
};

export const getThemeTransitionCSS = (): string => {
  return `transition: ${themeTransition.properties} ${themeTransition.duration} ${themeTransition.timing};`;
};
